/**
 * ImageGridBlock — multi-image gallery with a heading + column count.
 *
 * Perfect for menus, catalogs, product lookbooks, and portfolio grids.
 * • Heading (optional; hides if blank)
 * • 1–4 columns (creator picks — respects the choice on all viewports)
 * • Square / portrait / landscape / free aspect ratios
 * • Optional per-image caption
 * • Tap to zoom (lightbox) on the public page
 * • Rows with an empty URL are skipped automatically
 */
import { useState } from "react";
import MediaLightbox from "../MediaLightbox";
import { sizedImage } from "../../lib/imageUrl";

const ASPECT_CLS = {
  square:    "aspect-square",
  portrait:  "aspect-[3/4]",
  landscape: "aspect-[4/3]",
  wide:      "aspect-[16/9]",
  free:      "", // let the image dictate its own ratio
};

const COL_CLS = {
  1: "grid-cols-1",
  2: "grid-cols-2",
  3: "grid-cols-3",
  4: "grid-cols-4",
};

export default function ImageGridBlock({ block, editable = false }) {
  const c = block?.config || {};
  const heading = (c.heading || "").trim();
  const columns = Math.max(1, Math.min(4, Number(c.columns) || 2));
  const aspect = ASPECT_CLS[c.aspect] ?? ASPECT_CLS.square;
  const items = Array.isArray(c.items) ? c.items : [];
  const rows = items.filter((it) => (it && (it.url || "").trim()));
  const [zoomIndex, setZoomIndex] = useState(null);

  if (rows.length === 0 && !heading) return null;

  const gridCls = `grid gap-2 sm:gap-3 ${COL_CLS[columns] || COL_CLS[2]}`;

  const lightboxItems = rows.map((r) => ({
    kind: "image",
    url: r.url,
    caption: r.caption,
    alt: r.caption || "",
  }));

  return (
    <div
      className="ah-card image-grid-card p-4 sm:p-5"
      data-testid={`ah-block-image-grid-${block?.block_id || "preview"}`}
      style={{
        background: "var(--ah-block-bg)",
        color: "var(--ah-block-fg)",
        border: "var(--ah-block-border, 2px solid #111)",
        borderRadius: "var(--ah-block-radius, 0)",
        boxShadow: "var(--ah-block-shadow, none)",
      }}
    >
      {heading && (
        <h3
          className="font-black text-xl sm:text-2xl mb-3 leading-tight"
          style={{
            fontFamily: "var(--ah-title-family)",
            fontWeight: "var(--ah-title-weight, 900)",
            letterSpacing: "var(--ah-title-tracking, normal)",
          }}
          data-testid="image-grid-heading"
        >
          {heading}
        </h3>
      )}
      {rows.length > 0 ? (
        <div className={gridCls} data-testid="image-grid-items">
          {rows.map((it, i) => (
            <button
              key={i}
              type="button"
              onClick={() => (editable ? null : setZoomIndex(i))}
              className={`relative overflow-hidden border-2 border-current bg-white/40 group ${aspect}`}
              data-testid={`image-grid-item-${i}`}
              aria-label={it.caption || `Image ${i + 1}`}
            >
              <img
                src={sizedImage(it.url, 640)}
                alt={it.caption || `Image ${i + 1}`}
                loading="lazy"
                className="w-full h-full object-cover transition-transform group-hover:scale-105"
                onError={(e) => { e.currentTarget.style.opacity = "0.3"; }}
              />
              {it.caption && (
                <span className="absolute bottom-0 left-0 right-0 bg-black/70 text-white text-[11px] font-bold px-2 py-1 truncate">
                  {it.caption}
                </span>
              )}
            </button>
          ))}
        </div>
      ) : (
        <p className="text-sm opacity-60 text-center py-8" data-testid="image-grid-empty">
          Add photos in the editor to fill this grid.
        </p>
      )}

      {/* Lightbox with prev/next + swipe + keyboard nav (shared component) */}
      {zoomIndex !== null && !editable && (
        <MediaLightbox
          items={lightboxItems}
          startIndex={zoomIndex}
          onClose={() => setZoomIndex(null)}
          testid="image-grid-lightbox"
        />
      )}
    </div>
  );
}
